import type { SectionQuestion } from "./api";

export function questionKey(q: SectionQuestion): string {
  return q.key;
}

function parentAnswer(
  q: SectionQuestion,
  answers: Record<string, string>,
): string {
  if (!q.show_when) return "";
  return (answers[q.show_when.parent_key] ?? "").trim().toUpperCase();
}

/** Visible questions only — includes empty answers so submit matches preview. */
export function sectionAnswers(
  questions: SectionQuestion[],
  state: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  questions.forEach((q) => {
    if (!showQuestion(q, questions, state)) return;
    out[questionKey(q)] = state[questionKey(q)] ?? "";
  });
  return out;
}

export function showQuestion(
  q: SectionQuestion,
  _questions: SectionQuestion[],
  answers: Record<string, string>,
): boolean {
  if (!q.show_when) return true;
  const parentVal = parentAnswer(q, answers);
  const allowed = q.show_when.values.map((v) => v.trim().toUpperCase());
  if (allowed.includes(parentVal)) return true;
  return allowed.some((v) => parentVal.startsWith(`${v} `));
}

export type SavedSectionDraft = {
  answers: Record<string, string>;
  outlook: Record<string, string>;
  tier: string;
};

function recordSignature(values: Record<string, string>): string {
  return JSON.stringify(
    Object.keys(values)
      .sort()
      .map((key) => [key, values[key] ?? ""]),
  );
}

/** True when local draft differs from the last saved section state. */
export function hasUnsavedSectionDraft(
  questions: SectionQuestion[],
  answers: Record<string, string>,
  outlook: Record<string, string>,
  tier: string,
  saved: SavedSectionDraft,
  autoTier: boolean,
): boolean {
  const currentAnswers = sectionAnswers(questions, answers);
  const savedAnswers = sectionAnswers(questions, saved.answers);
  if (recordSignature(currentAnswers) !== recordSignature(savedAnswers)) {
    return true;
  }

  for (const question of questions) {
    if (!question.outlook_48h || !showQuestion(question, questions, answers)) {
      continue;
    }
    const key = questionKey(question);
    if ((outlook[key] ?? "") !== (saved.outlook[key] ?? "")) {
      return true;
    }
  }

  if (!autoTier && tier.trim() !== (saved.tier ?? "").trim()) {
    return true;
  }

  return false;
}
